import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ServiceLocationContext = createContext(null);

// Property (Service Location) context for the customer dashboard.
//
// `customerId` must be the customer logged in NOW: App.js's DashboardRoute reads
// it at route mount and keys this provider by it, so a different customer always
// gets a fresh provider with no property state carried over (2026-10-15).
//
// status: 'loading' -> 'ready' | 'error'. An error (403 session mismatch,
// network, 5xx) is surfaced, never treated as "no properties": the dashboard must
// not load property-specific data with a guessed or default property.
export function ServiceLocationProvider({ customerId, children }) {
  const [locations, setLocations] = useState([]);
  const [selected, setSelectedState] = useState(null);
  const [status, setStatus] = useState(customerId ? 'loading' : 'error');
  const [errorCode, setErrorCode] = useState(customerId ? null : 'no_session');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!customerId) { setStatus('error'); setErrorCode('no_session'); return undefined; }
    let cancelled = false;
    setStatus('loading');
    setErrorCode(null);
    fetch(`${BACKEND_URL}/api/customer/${encodeURIComponent(customerId)}/service-locations`)
      .then(async (r) => {
        if (!r.ok) {
          const err = new Error(`HTTP ${r.status}`);
          err.code = r.status === 403 ? 'forbidden' : 'failed';
          throw err;
        }
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        const locs = Array.isArray(data.data) ? data.data : [];
        setLocations(locs);
        // Zero or one location: never auto-select. Historical records may
        // predate location-tagging and have no custom_service_location set,
        // so forwarding the customer's sole location as a filter would
        // silently exclude them (ERPNext `=` doesn't match NULL). Leaving
        // `selected` null means no service_location param is ever sent for
        // 0/1-property customers -- matches pre-feature behavior exactly.
        setSelectedState((prev) => (prev && locs.some((l) => l.name === prev) ? prev : null));
        setStatus('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        setLocations([]);
        setSelectedState(null);
        setErrorCode(e.code || 'failed');
        setStatus('error');
      });
    return () => { cancelled = true; };
  }, [customerId, attempt]);

  // Only a property from THIS customer's own list can ever be selected.
  const setSelected = useCallback((name) => {
    setSelectedState((prev) => {
      if (name === null) return null;
      return locations.some((l) => l.name === name) ? name : prev;
    });
  }, [locations]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const loading = status === 'loading';
  const ready = status === 'ready';
  // needsPicker: a real choice exists and none has been made yet -- Dashboard
  // renders <PropertyPicker/> instead of anything property-specific.
  const needsPicker = ready && locations.length > 1 && !selected;
  // propertyReady: safe to load property-specific data (the property is known,
  // or the customer has 0/1 properties so no filter applies).
  const propertyReady = ready && (locations.length <= 1 || !!selected);

  return (
    <ServiceLocationContext.Provider
      value={{ customerId, locations, selected, setSelected, loading, ready, needsPicker, propertyReady, status, errorCode, retry }}
    >
      {children}
    </ServiceLocationContext.Provider>
  );
}

export function useServiceLocation() {
  const ctx = useContext(ServiceLocationContext);
  if (!ctx) throw new Error('useServiceLocation must be used within a ServiceLocationProvider');
  return ctx;
}
